import { createContext, useContext, useEffect, useState } from "react";
import { authClient } from "@/lib/auth-client";

interface User {
  id: string;
  name: string | null;
  email: string;
  image: string | null;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInstanceAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signInWithProvider: (provider: string, callbackURL?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchSession(): Promise<User | null> {
  try {
    const res = await fetch("/api/auth/get-session", { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.user ?? null;
  } catch {
    return null;
  }
}

async function fetchMe(): Promise<{ isInstanceAdmin: boolean }> {
  try {
    const res = await fetch("/api/me", { credentials: "include" });
    if (!res.ok) return { isInstanceAdmin: false };
    const data = await res.json();
    return { isInstanceAdmin: !!data.isInstanceAdmin };
  } catch {
    return { isInstanceAdmin: false };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isInstanceAdmin, setIsInstanceAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchSession(), fetchMe()])
      .then(([sessionUser, me]) => {
        setUser(sessionUser);
        setIsInstanceAdmin(me.isInstanceAdmin);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error?: string }> => {
    try {
      const res = await fetch("/api/auth/sign-in/email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { error: data.error?.message || data.message || "Sign in failed" };
      }
      if (data.user) {
        setUser(data.user);
        fetchMe().then((me) => setIsInstanceAdmin(me.isInstanceAdmin));
      }
      return {};
    } catch {
      return { error: "Network error" };
    }
  };

  const signInWithProvider = async (provider: string, callbackURL?: string) => {
    // Server configures Entra via genericOAuth, so the client must call
    // signIn.oauth2 (which auto-navigates) rather than signIn.social
    // (which returns the URL but doesn't follow it for generic providers).
    // callbackURL override: the MCP OAuth flow lands back on /auth with the
    // pending authorize query so it can resume (see Auth.tsx).
    await authClient.signIn.oauth2({
      providerId: provider,
      callbackURL: callbackURL ?? `${window.location.origin}/`,
    });
  };

  const signOut = async () => {
    try {
      await fetch("/api/auth/sign-out", { method: "POST", credentials: "include" });
    } catch {
      // ignore
    }
    setUser(null);
    setIsInstanceAdmin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isInstanceAdmin,
        signIn,
        signInWithProvider,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
